import { Component } from 'react';
import Helmet from 'react-helmet';
import { Link } from 'react-router';
import type { Location } from 'history';
import Locator from '../../../../shared/Locator';
import { window } from '../../../globals';
import ProjectStore from '../../../stores/ProjectStore';
import type { IAsync, Project, Revision } from '../../../types';
import { nuc, openInNewTab, trimString, isProjectInCurrentOrg } from '../../../utils';
import type { IconClassName } from '../../generic/Icon';
import Icon, { icons } from '../../generic/Icon';
import Tooltip from '../../generic/Tooltip';
import ProjectTypeIcon from '../generic/ProjectTypeIcon';
import ProjectNavigation from '../overview/ProjectNavigation';
import RevisionDropdown from '../RevisionDropdown';
import Flex from '../../generic/Flex';
import StatusBadge from '../settingsv2/general/BadgePanel/StatusBadge';
import BadgeModal from '../settingsv2/general/BadgePanel/BadgeModal';
import type { BadgeType } from '../settingsv2/general/BadgePanel/types';
import { WithDialogContext } from '../../../contexts/DialogContext';
import type { DialogState } from '../../../contexts/DialogContext';
import SBOMAffordances from './SBOMAffordances';

type Props = {
  notifications: any[];
  params: any;
  revision?: Revision;
  location: Location<{ revisionScanId?: string }>;
  dialogContext: DialogState;
  showBranchDropdown?: boolean;
};

type State = {
  project: IAsync<Project>;
  issueMenuOpen: boolean;
  shouldShowBadgeModal: boolean;
  badgeModalTab: BadgeType;
};

class DisplayProjectNav extends Component<Props, State> {
  constructor(props) {
    super(props);
    this.state = {
      project: {
        status: {
          loading: true,
          errors: [],
        },
        data: {},
      },
      issueMenuOpen: false,
      shouldShowBadgeModal: false,
      badgeModalTab: 'licenseShield',
    };
  }

  static defaultProps: Partial<Props> = {
    notifications: [],
  };

  static NavIcon = ({ icon, ...rest }: { icon: IconClassName }) => (
    <span className="nav-icon-tooltip" {...rest}>
      <Icon icon={icon} />
    </span>
  );

  unsubscribeProject: Function;

  componentDidMount() {
    this.unsubscribeProject = (ProjectStore as any).listen(this.onProjectUpdate);
    this.setState({
      project: (ProjectStore as any).getProject(nuc(this.props.params.project_id)) || {
        status: { loading: true },
        data: {},
      },
    });
  }

  componentWillUnmount() {
    if (this.unsubscribeProject) {
      this.unsubscribeProject();
    }
  }

  onProjectUpdate = () => {
    this.setState({
      project: (ProjectStore as any).getProject(nuc(this.props.params.project_id)) || {
        status: { loading: true },
        data: {},
      },
    });
  };

  onNavigateProjectHome = () => {
    window.open(this.state.project.data.url, '_blank', 'noopener=true');
  };

  onNavigateRevisionLink = () => {
    if (!this.props.revision?.link) {
      return;
    }
    this.props.dialogContext.onConfirm({
      title: 'Leaving Fossa',
      body: (
        <div className="modal-link-navigate-text">
          By clicking this link, you will be navigated away from FOSSA to <b>{this.props.revision.link}</b>
        </div>
      ),
      onConfirm: () => openInNewTab(this.props.revision.link),
    });
  };

  getReference = () => {
    if (this.props.params.reference) {
      return this.props.params.reference;
    }

    if (this.state.project && this.state.project.data) {
      return this.state.project.data.default_branch;
    }

    return 'master';
  };

  getRevision = () => {
    if (this.props.params.revision_id) {
      return this.props.params.revision_id;
    }

    if (this.state.project?.data?.last_analyzed_revision) {
      return new Locator(this.state.project.data.last_analyzed_revision).revision.value;
    }

    return '';
  };

  renderTitle = (revisionUrl) => {
    const isLoading = !this.state.project || !this.state.project.data || this.state.project.status.loading;
    const isProjectNotFound = this.state.project?.status?.error?.httpStatusCode === 404;
    const isCurrentOrgProject = isProjectInCurrentOrg(this.state.project.data?.organizationId ?? 0);
    return (
      <div className="project-title-section">
        <h1 className="nav-project-description">
          <span className="animated fadeInLeft">
            {!isLoading ? (
              <span>
                {this.state.project.data.title.length > 50 ? (
                  <Tooltip tooltip={this.state.project.data.title}>
                    <Link
                      style={{ color: '#fff' }}
                      className="project-title-link"
                      to={{
                        pathname:
                          isLoading || isCurrentOrgProject ? revisionUrl : `${revisionUrl}/preview`,
                      }}
                    >
                      {this.state.project.data.title}
                    </Link>
                  </Tooltip>
                ) : (
                  <Link
                    style={{ color: '#fff' }}
                    className="project-title-link"
                    to={{
                      pathname: isLoading || isCurrentOrgProject ? revisionUrl : `${revisionUrl}/preview`,
                    }}
                  >
                    {this.state.project.data.title}
                  </Link>
                )}
                <div className="project-icons">
                  {!this.state.project.data.public && (
                    <Tooltip placement="bottom" tooltip="Private">
                      <DisplayProjectNav.NavIcon icon={icons.lock} />
                    </Tooltip>
                  )}
                  <ProjectTypeIcon project={this.state.project.data} className="nav-icon-tooltip" placement="bottom" />
                  {this.state.project.data.url && (
                    <Tooltip
                      placement="bottom"
                      tooltip={
                        <div id="gotoProjectHomeTooltip">
                          <b>Go to Project Website</b>
                          <br />
                          Note: Information at this link may represent a different version.
                        </div>
                      }
                    >
                      <a onClick={this.onNavigateProjectHome}>
                        <DisplayProjectNav.NavIcon icon={icons.home} />
                      </a>
                    </Tooltip>
                  )}
                  {this.props.revision && this.props.revision.link && (
                    <Tooltip
                      placement="bottom"
                      tooltip={
                        <div id="gotoBuildLinkTooltip">
                          <b>Go to Provided Link</b>
                          <br />
                          Note: This link was provided from FOSSA CLI
                        </div>
                      }
                    >
                      <a onClick={this.onNavigateRevisionLink}>
                        <DisplayProjectNav.NavIcon icon={icons.link} />
                      </a>
                    </Tooltip>
                  )}
                </div>
                <span className="pull-right">
                  <Tooltip id="status-badge" tooltip="Click for badge embed code" placement="bottom">
                    <div className="inline-block">
                      <Flex direction="column" align="flex-start" gap="8px">
                        <StatusBadge
                          locator={this.props.params.project_id}
                          badgeType="licenseShield"
                          onClick={() => this.setState({ shouldShowBadgeModal: true, badgeModalTab: 'licenseShield' })}
                        />
                        {this.state.project?.data?.securityIssueScanningEnabled && (
                          <StatusBadge
                            locator={this.props.params.project_id}
                            badgeType="securityShield"
                            onClick={() =>
                              this.setState({ shouldShowBadgeModal: true, badgeModalTab: 'securityShield' })
                            }
                          />
                        )}
                      </Flex>
                    </div>
                  </Tooltip>
                  <BadgeModal
                    locator={this.props.params.project_id}
                    isSecurityEnabled={this.state.project?.data?.securityIssueScanningEnabled}
                    shouldShow={this.state.shouldShowBadgeModal}
                    defaultTab={this.state.badgeModalTab}
                    onHide={() => this.setState({ shouldShowBadgeModal: false })}
                  />
                </span>
              </span>
            ) : isProjectNotFound ? (
              <i className="subtitle">Not Found</i>
            ) : (
              <i className="subtitle">loading...</i>
            )}
          </span>
        </h1>
        <Flex justify="flex-start" gap="10px">
          <h1 className="nav-project-description">
            {this.props.params.reference && (this.props.showBranchDropdown || this.props.revision) && (
              <span className="navigation-revision-selector animated fadeInLeft fast">
                <span
                  style={{
                    position: 'relative',
                    top: '-2px',
                    fontSize: '.8em',
                  }}
                >
                  <ProjectNavigation
                    project={this.state.project}
                    reference={this.props.params.reference}
                    selectedRevision={this.props.revision}
                    dropdownOnly
                  />
                </span>
              </span>
            )}
          </h1>
          <p
            className="animated fadeInDown fast"
            style={{
              zIndex: 1,
              position: 'relative',
              color: '#fff',
              marginBottom: 0,
            }}
          >
            {this.props.params.reference && this.props.revision ? (
              <span
                style={{
                  position: 'relative',
                  marginLeft: '-6px',
                  marginTop: '-6px',
                }}
              >
                <RevisionDropdown
                  project={this.state.project}
                  reference={this.props.params.reference}
                  referenceType={this.props.params.reference_type}
                  branch={this.props.params.branch}
                  tag={this.props.params.tag}
                  selectedRevision={this.props.revision}
                />
              </span>
            ) : !isLoading && this.state.project.data.description ? (
              trimString(this.state.project.data.description, 175)
            ) : (
              'No description available'
            )}
          </p>
          <SBOMAffordances locator={this.props.revision?.locator || ''} />
        </Flex>
      </div>
    );
  };

  render() {
    const reference = this.getReference();
    const referenceType = this.props.params.reference_type || 'branch';
    const baseUrl = `/projects/${nuc(this.props.params.project_id)}/`;
    const revision = this.getRevision();
    const revisionUrl = `${baseUrl}refs/${referenceType}/${nuc(reference)}/${nuc(revision)}`;
    const helmetTitle = this.state.project.data ? this.state.project.data.title : 'Loading ...';

    return (
      <div>
        <Helmet title={`${helmetTitle} - Projects`} />
        {this.renderTitle(revisionUrl)}
      </div>
    );
  }
}

export default WithDialogContext(DisplayProjectNav);
